import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './templateselector.module.scss';

interface ITemplatePreviewProps {
  invoiceId: string;
  templateId: string;
}

function getHtml(invoiceId: string, templateId: string) {
  return `${process.env.SERVER_URL}/api/invoice/html/${invoiceId}/?template_id=${templateId}`;
}

const TemplatePreview = ({ invoiceId, templateId }: ITemplatePreviewProps) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const htmlUrl = getHtml(invoiceId, templateId);

  // Calculate responsive scale based on container width only
  const calculateResponsiveScale = useCallback(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const targetWidth = 645; // Original iframe width
      const calculatedScale = Math.min(containerWidth / targetWidth, 1);

      return calculatedScale;
    }
    return 1;
  }, []);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Set initial scale when component mounts or container size changes
  useEffect(() => {
    const initialScale = calculateResponsiveScale();
    setScale(initialScale);
    setPosition({ x: 0, y: 0 }); // Reset position when scale changes
  }, [calculateResponsiveScale]);

  // Handle mouse events for pan and zoom
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isMobile) return; // Only enable interaction on mobile
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    },
    [position, isMobile],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && isMobile) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, dragStart, isMobile],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle touch events for mobile
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
      }
    },
    [position],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1 && isDragging) {
        const touch = e.touches[0];
        setPosition({
          x: touch.clientX - dragStart.x,
          y: touch.clientY - dragStart.y,
        });
      }
    },
    [isDragging, dragStart],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Zoom controls
  const zoomIn = useCallback(() => {
    setScale(prev => Math.min(prev * 1.2, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setScale(prev => Math.max(prev / 1.2, 0.3));
  }, []);

  const resetView = useCallback(() => {
    const initialScale = calculateResponsiveScale();
    setScale(initialScale);
    setPosition({ x: 0, y: 0 });
  }, [calculateResponsiveScale]);

  // Add viewport meta tag injection for iframe content
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleIframeLoad = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          // Check if viewport meta tag exists, if not add it
          let viewportMeta = iframeDoc.querySelector('meta[name="viewport"]');
          if (!viewportMeta) {
            viewportMeta = iframeDoc.createElement('meta');
            viewportMeta.setAttribute('name', 'viewport');
            viewportMeta.setAttribute(
              'content',
              'width=device-width, initial-scale=1.0, user-scalable=yes',
            );
            iframeDoc.head?.appendChild(viewportMeta);
          }

          // Add mobile-friendly styles
          const style = iframeDoc.createElement('style');
          style.textContent = `
            body { 
              margin: 0; 
              padding: 10px; 
              box-sizing: border-box;
              min-width: unset !important;
              width: auto !important;
            }
            * { 
              max-width: 100% !important; 
              box-sizing: border-box; 
            }
            table { 
              width: 100% !important; 
              table-layout: fixed !important; 
            }
            img { 
              max-width: 100% !important; 
              height: auto !important; 
            }
          `;
          iframeDoc.head?.appendChild(style);
        }
      } catch (e) {
        // Cross-origin restrictions - can't modify iframe content
        console.log('Cannot modify iframe content due to cross-origin restrictions');
      }
    };

    iframe.addEventListener('load', handleIframeLoad);
    return () => iframe.removeEventListener('load', handleIframeLoad);
  }, [htmlUrl]);

  return (
    <div ref={containerRef} className={`full-height center ${styles.outerIframeWrapper}`}>
      {/* Interactive iframe container */}
      <div
        className={`${styles.iframeContainer} ${
          isDragging ? styles.dragging : isMobile ? styles.mobile : styles.desktop
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <iframe
          key={htmlUrl}
          ref={iframeRef}
          title="invoice-preview"
          src={htmlUrl}
          className={`${styles.mainIframe} ${isDragging ? styles.nointeration : styles.interaction}`}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          }}
        />
      </div>

      {/* Mobile controls */}
      {isMobile && (
        <div className={styles.mobileControls}>
          <button onClick={zoomIn} className={styles.controlButton}>
            +
          </button>
          <button onClick={zoomOut} className={styles.controlButton}>
            -
          </button>
          <button onClick={resetView} className={`${styles.controlButton} ${styles.resetButton}`}>
            Reset
          </button>
        </div>
      )}

      {/* Scale indicator for mobile */}
      {isMobile && (
        <div className={styles.scaleIndicator}>
          {Math.round(scale * 100)}%
        </div>
      )}
    </div>
  );
};

export default TemplatePreview;
